/**
 * NEXUS UI - Sparkline
 * Inline mini chart with glow effect
 */

import React, { useMemo } from 'react';
import type { BaseProps, NexusVariant, DataPoint } from './types';

export interface SparklineProps extends BaseProps {
  data: number[] | DataPoint[];
  variant?: NexusVariant;
  width?: number;
  height?: number;
  showDot?: boolean;
  showArea?: boolean;
  strokeWidth?: number;
  animated?: boolean;
}

const variantColors: Record<NexusVariant, { stroke: string; fill: string; glow: string }> = {
  cyan: { stroke: '#00d4ff', fill: 'rgba(0, 212, 255, 0.2)', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { stroke: '#3b82f6', fill: 'rgba(59, 130, 246, 0.2)', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { stroke: '#a855f7', fill: 'rgba(168, 85, 247, 0.2)', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { stroke: '#00ff88', fill: 'rgba(0, 255, 136, 0.2)', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { stroke: '#ffaa00', fill: 'rgba(255, 170, 0, 0.2)', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { stroke: '#ff3366', fill: 'rgba(255, 51, 102, 0.2)', glow: 'rgba(255, 51, 102, 0.6)' },
};

export const Sparkline: React.FC<SparklineProps> = ({
  data,
  variant = 'cyan',
  width = 120,
  height = 40,
  showDot = true,
  showArea = true,
  strokeWidth = 2,
  animated = true,
  className = '',
  style,
}) => {
  const colors = variantColors[variant];
  const padding = 4;

  const values = useMemo(() => {
    return data.map(d => (typeof d === 'number' ? d : d.value));
  }, [data]);

  const { path, areaPath, lastPoint } = useMemo(() => {
    if (values.length < 2) return { path: '', areaPath: '', lastPoint: null };

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    const points = values.map((value, index) => {
      const x = padding + (index / (values.length - 1)) * (width - padding * 2);
      const y = height - padding - ((value - min) / range) * (height - padding * 2);
      return { x, y };
    });

    // Create smooth curve using cardinal spline
    const linePath = points.reduce((acc, point, index) => {
      if (index === 0) return `M ${point.x} ${point.y}`;

      const prev = points[index - 1];
      const cp1x = prev.x + (point.x - prev.x) / 3;
      const cp2x = prev.x + 2 * (point.x - prev.x) / 3;

      return `${acc} C ${cp1x} ${prev.y}, ${cp2x} ${point.y}, ${point.x} ${point.y}`;
    }, '');

    const areaPath = `${linePath} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;

    return {
      path: linePath,
      areaPath,
      lastPoint: points[points.length - 1],
    };
  }, [values, width, height, padding]);

  const gradientId = `sparkline-gradient-${variant}-${Math.random().toString(36).slice(2, 9)}`;
  const glowId = `sparkline-glow-${variant}-${Math.random().toString(36).slice(2, 9)}`;

  return (
    <div
      className={`nx-sparkline ${className}`}
      style={{
        '--sparkline-color': colors.stroke,
        '--sparkline-glow': colors.glow,
        display: 'inline-block',
        ...style,
      } as React.CSSProperties}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.stroke} stopOpacity="0.4" />
            <stop offset="100%" stopColor={colors.stroke} stopOpacity="0" />
          </linearGradient>
          <filter id={glowId}>
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Area fill */}
        {showArea && (
          <path
            className="nx-sparkline__area"
            d={areaPath}
            fill={`url(#${gradientId})`}
          />
        )}

        {/* Line */}
        <path
          className="nx-sparkline__line"
          d={path}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={`url(#${glowId})`}
          style={{
            strokeDasharray: animated ? '1000' : 'none',
            strokeDashoffset: animated ? '1000' : '0',
            animation: animated ? 'nx-border-trace 1.5s ease-out forwards' : 'none',
          }}
        />

        {/* End dot */}
        {showDot && lastPoint && (
          <>
            {/* Outer ring - static */}
            <circle
              cx={lastPoint.x}
              cy={lastPoint.y}
              r={6}
              fill="none"
              stroke={colors.stroke}
              strokeWidth={1}
              opacity={0.3}
            />
            {/* Main dot */}
            <circle
              className="nx-sparkline__dot"
              cx={lastPoint.x}
              cy={lastPoint.y}
              r={3}
              fill={colors.stroke}
              filter={`url(#${glowId})`}
            />
          </>
        )}
      </svg>
    </div>
  );
};

export default Sparkline;
